import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import config from 'config';

/** Mirrors `monitoring.gateway.ts` so client and server can't drift on a bare string literal. */
export const MONITOR_EVENTS = {
  join: 'join:test',
  leave: 'leave:test',
  started: 'attempt:started',
  progress: 'attempt:progress',
  violation: 'attempt:violation',
  submitted: 'attempt:submitted',
} as const;

export type TickerKind = 'started' | 'violation' | 'submitted';

export interface TickerEvent {
  id: string | number;
  attemptId?: string;
  candidateName?: string;
  kind: TickerKind;
  /** Violation event type (TAB_SWITCH / FULLSCREEN_EXIT / …), when present. */
  type?: string;
  at: number;
}

const MAX_TICKER = 50;

export interface UseMonitoringSocketResult {
  connected: boolean;
  ticker: TickerEvent[];
}

/** Re-joins the room on reconnect and testId change. Cleanup removes every listener AND nulls the ref. */
export function useMonitoringSocket(
  testId: string | undefined,
  onChange: () => void,
  initialEvents: TickerEvent[] = [],
): UseMonitoringSocketResult {
  const [connected, setConnected] = useState(false);
  const [ticker, setTicker] = useState<TickerEvent[]>([]);
  const socketRef = useRef<Socket | null>(null);
  const testIdRef = useRef(testId);
  const onChangeRef = useRef(onChange);
  const seqRef = useRef(0);
  testIdRef.current = testId;
  onChangeRef.current = onChange;

  // Hydrate ticker from initial persisted events
  useEffect(() => {
    if (initialEvents && initialEvents.length > 0) {
      setTicker((prev) => {
        if (prev.length === 0) {
          return initialEvents.slice(0, MAX_TICKER);
        }
        const existingIds = new Set(prev.map((e) => String(e.id)));
        const newFromInitial = initialEvents.filter((e) => !existingIds.has(String(e.id)));
        if (newFromInitial.length === 0) return prev;
        return [...prev, ...newFromInitial].sort((a, b) => b.at - a.at).slice(0, MAX_TICKER);
      });
    }
  }, [initialEvents]);

  useEffect(() => {
    const push = (kind: TickerKind, payload: { attemptId?: string; type?: string }) => {
      seqRef.current += 1;
      const entry: TickerEvent = {
        id: `live-${seqRef.current}-${Date.now()}`,
        attemptId: payload?.attemptId,
        kind,
        type: payload?.type,
        at: Date.now(),
      };
      setTicker((prev) => [entry, ...prev.filter((e) => e.id !== entry.id)].slice(0, MAX_TICKER));
    };

    const token = localStorage.getItem(config.api.access_token_key);
    const socket = io(`${config.api.base_url}/monitoring`, {
      transports: ['websocket'],
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      // Re-emitted on every reconnect too, so room membership survives a drop.
      if (testIdRef.current) socket.emit(MONITOR_EVENTS.join, testIdRef.current);
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', () => setConnected(false));
    socket.on(MONITOR_EVENTS.started, (p) => { onChangeRef.current(); push('started', p ?? {}); });
    socket.on(MONITOR_EVENTS.progress, () => onChangeRef.current());
    socket.on(MONITOR_EVENTS.violation, (p) => { onChangeRef.current(); push('violation', p ?? {}); });
    socket.on(MONITOR_EVENTS.submitted, (p) => { onChangeRef.current(); push('submitted', p ?? {}); });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  // Re-join when the monitored test changes (without re-subscribing the socket).
  useEffect(() => {
    const socket = socketRef.current;
    if (socket?.connected && testId) socket.emit(MONITOR_EVENTS.join, testId);
  }, [testId]);

  return { connected, ticker };
}

export default useMonitoringSocket;
